/**
 * Landlord Onboarding Workflow Types
 * Tracks individual landlord onboarding through the 5-stage white-glove process
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Onboarding stages (5-stage process from ONBOARDING_WORKFLOW.md)
 */
export type LandlordOnboardingStage =
  | 'discovery' // Stage 1: Discovery & Data Collection
  | 'provisioning' // Stage 2: Account Provisioning
  | 'welcome_sent' // Stage 3: Welcome Email Delivery
  | 'first_login' // Stage 4: First Login Flow
  | 'follow_up'; // Stage 5: Post-Onboarding Follow-Up

/**
 * Onboarding status
 */
export type LandlordOnboardingStatus =
  | 'not_started' // Discovery call not yet scheduled
  | 'in_progress' // Actively onboarding
  | 'completed' // All stages complete
  | 'blocked' // Issues preventing progress
  | 'abandoned'; // Landlord unresponsive

/**
 * Stage status
 */
export type StageStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'blocked'
  | 'skipped';

/**
 * Blocker severity
 */
export type BlockerSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Activity type
 */
export type ActivityType =
  | 'note'
  | 'email'
  | 'call'
  | 'meeting'
  | 'milestone'
  | 'blocker'
  | 'resolution';

/**
 * Stage detail with tracking
 */
export interface StageDetail {
  stage: LandlordOnboardingStage;
  status: StageStatus;
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  assignedTo?: string; // User ID of specialist
  assignedToName?: string;
  notes?: string;
  checklist: ChecklistItem[];
}

/**
 * Checklist item for stage tracking
 */
export interface ChecklistItem {
  id: string;
  description: string;
  completed: boolean;
  required: boolean;
  completedAt?: Timestamp;
  completedBy?: string;
}

/**
 * Blocker preventing progress
 */
export interface OnboardingBlocker {
  id: string;
  stage: LandlordOnboardingStage;
  severity: BlockerSeverity;
  description: string;
  impact: string;
  resolution?: string;
  createdAt: Timestamp;
  createdBy: string;
  createdByName: string;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolvedByName?: string;
}

/**
 * Activity/interaction log
 */
export interface OnboardingActivity {
  id: string;
  type: ActivityType;
  subject: string;
  description?: string;
  performedBy: string;
  performedByName: string;
  timestamp: Timestamp;
}

/**
 * Landlord onboarding record
 */
export interface LandlordOnboarding {
  id: string;
  landlordId: string;
  landlordName: string;
  landlordEmail: string;
  landlordPhone?: string;
  landlordCompany?: string;

  // Onboarding metadata
  status: LandlordOnboardingStatus;
  currentStage: LandlordOnboardingStage;
  progress: number; // 0-100%

  // Timing
  discoveryCallDate?: Timestamp; // When discovery call happened
  targetCompletionDate: Timestamp; // When onboarding should be done
  actualCompletionDate?: Timestamp; // When it was actually completed

  // Data collected
  propertyCount: number;
  unitCount: number;
  tenantCount: number;
  timezone: string;
  currency: string;

  // Stage tracking
  stages: StageDetail[];
  blockers: OnboardingBlocker[];
  activities: OnboardingActivity[];

  // Metrics
  timeToFirstLogin?: number; // Hours from welcome email to first login
  timeToGmailConnection?: number; // Hours from first login to Gmail OAuth
  timeToFirstPayment?: number; // Hours from Gmail connection to first payment detected

  // Satisfaction
  npsScore?: number; // 0-10
  npsComment?: string;
  satisfactionRating?: number; // 1-5 stars
  satisfactionFeedback?: string;

  // Metadata
  assignedTo?: string; // Customer success specialist ID
  assignedToName?: string;
  createdAt: Timestamp;
  createdBy: string;
  createdByName: string;
  updatedAt: Timestamp;
  updatedBy: string;
  updatedByName: string;
}

/**
 * Statistics for landlord onboarding
 */
export interface LandlordOnboardingStats {
  totalOnboardings: number;
  activeOnboardings: number;
  completedThisMonth: number;
  blockedOnboardings: number;
  
  // Average metrics
  avgTimeToFirstLogin: number; // Hours
  avgTimeToGmailConnection: number; // Hours
  avgCompletionTime: number; // Days
  
  // Success rates
  firstLoginRate24h: number; // % who login within 24h
  gmailConnectionRate48h: number; // % who connect Gmail within 48h
  completionRate: number; // % who complete all stages
  
  // NPS
  avgNpsScore: number;
  promoters: number; // NPS 9-10
  passives: number; // NPS 7-8
  detractors: number; // NPS 0-6
  
  // By stage
  byStage: Record<LandlordOnboardingStage, number>;
  
  // By specialist
  bySpecialist: Record<string, {
    name: string;
    count: number;
    completionRate: number;
    avgNps: number;
  }>;
}

/**
 * Initialize stage checklist based on stage type
 */
export function initializeStageChecklist(stage: LandlordOnboardingStage): ChecklistItem[] {
  switch (stage) {
    case 'discovery':
      return [
        { id: 'schedule_call', description: 'Schedule discovery call', completed: false, required: true },
        { id: 'landlord_profile', description: 'Collect landlord profile (name, email, phone, company)', completed: false, required: true },
        { id: 'property_data', description: 'Gather property details (addresses, unit counts)', completed: false, required: true },
        { id: 'tenant_roster', description: 'Document tenant roster (names, emails, rent amounts)', completed: false, required: true },
        { id: 'payment_prefs', description: 'Confirm payment methods and preferences', completed: false, required: true },
        { id: 'notification_prefs', description: 'Set notification preferences', completed: false, required: false },
        { id: 'demo_portal', description: 'Demo portal features (screen share)', completed: false, required: false },
      ];
    
    case 'provisioning':
      return [
        { id: 'create_account', description: 'Create Firebase Auth account', completed: false, required: true },
        { id: 'generate_password', description: 'Generate secure temporary password', completed: false, required: true },
        { id: 'init_firestore', description: 'Initialize Firestore records (landlords collection)', completed: false, required: true },
        { id: 'import_properties', description: 'Import properties and units', completed: false, required: true },
        { id: 'import_tenants', description: 'Import tenant roster', completed: false, required: true },
        { id: 'set_preferences', description: 'Configure system preferences (timezone, currency)', completed: false, required: true },
      ];
    
    case 'welcome_sent':
      return [
        { id: 'send_email', description: 'Send welcome email with credentials', completed: false, required: true },
        { id: 'email_delivered', description: 'Confirm email delivered (no bounce)', completed: false, required: true },
        { id: 'update_crm', description: 'Update CRM status to "Onboarded - Pending First Login"', completed: false, required: false },
      ];
    
    case 'first_login':
      return [
        { id: 'first_login', description: 'Landlord logged in with temporary password', completed: false, required: true },
        { id: 'password_reset', description: 'Created permanent password', completed: false, required: true },
        { id: 'email_verified', description: 'Email address verified', completed: false, required: true },
        { id: 'review_properties', description: 'Reviewed properties and units', completed: false, required: true },
        { id: 'review_tenants', description: 'Reviewed tenant roster', completed: false, required: true },
        { id: 'gmail_connected', description: 'Connected Gmail account (OAuth)', completed: false, required: true },
        { id: 'set_notifications', description: 'Set notification preferences', completed: false, required: true },
        { id: 'onboarding_complete', description: 'Marked onboarding as complete', completed: false, required: true },
      ];
    
    case 'follow_up':
      return [
        { id: 'check_gmail', description: 'Verify Gmail integration working', completed: false, required: true },
        { id: 'check_payments', description: 'Confirm payments detected (if rent due)', completed: false, required: true },
        { id: 'check_logins', description: 'User logged in at least 2 times', completed: false, required: true },
        { id: 'send_followup', description: 'Send 24h follow-up email', completed: false, required: true },
        { id: 'manual_review', description: 'Manual review (48h check)', completed: false, required: true },
        { id: 'collect_nps', description: 'Collect NPS feedback', completed: false, required: false },
        { id: 'update_crm', description: 'Update CRM status to "Active"', completed: false, required: false },
      ];
  }
}

/**
 * Initialize all stages for new onboarding
 */
export function initializeLandlordOnboardingStages(): StageDetail[] {
  const stages: LandlordOnboardingStage[] = [
    'discovery',
    'provisioning',
    'welcome_sent',
    'first_login',
    'follow_up',
  ];

  return stages.map(stage => ({
    stage,
    status: stage === 'discovery' ? 'pending' : 'pending',
    checklist: initializeStageChecklist(stage),
  }));
}

/**
 * Get display name for stage
 */
export function getStageDisplayName(stage: LandlordOnboardingStage): string {
  const names: Record<LandlordOnboardingStage, string> = {
    discovery: 'Discovery & Data Collection',
    provisioning: 'Account Provisioning',
    welcome_sent: 'Welcome Email Sent',
    first_login: 'First Login Flow',
    follow_up: 'Post-Onboarding Follow-Up',
  };
  return names[stage];
}

/**
 * Get stage order (for progression)
 */
export function getStageOrder(stage: LandlordOnboardingStage): number {
  const order: Record<LandlordOnboardingStage, number> = {
    discovery: 1,
    provisioning: 2,
    welcome_sent: 3,
    first_login: 4,
    follow_up: 5,
  };
  return order[stage];
}

/**
 * Get next stage in sequence
 */
export function getNextStage(currentStage: LandlordOnboardingStage): LandlordOnboardingStage | null {
  const stages: LandlordOnboardingStage[] = [
    'discovery',
    'provisioning',
    'welcome_sent',
    'first_login',
    'follow_up',
  ];
  
  const currentIndex = stages.indexOf(currentStage);
  if (currentIndex === -1 || currentIndex === stages.length - 1) {
    return null;
  }
  
  return stages[currentIndex + 1];
}

/**
 * Calculate progress percentage based on completed stages
 */
export function calculateProgress(stages: StageDetail[]): number {
  if (stages.length === 0) return 0;
  
  const completedCount = stages.filter(s => s.status === 'completed').length;
  return Math.round((completedCount / stages.length) * 100);
}

/**
 * Get stage status color
 */
export function getStageStatusColor(status: StageStatus): string {
  const colors: Record<StageStatus, string> = {
    pending: 'gray',
    in_progress: 'blue',
    completed: 'green',
    blocked: 'red',
    skipped: 'orange',
  };
  return colors[status];
}

/**
 * Get blocker severity color
 */
export function getBlockerSeverityColor(severity: BlockerSeverity): string {
  const colors: Record<BlockerSeverity, string> = {
    low: 'gray',
    medium: 'orange',
    high: 'red',
    critical: 'purple',
  };
  return colors[severity];
}

/**
 * Get NPS category
 */
export function getNPSCategory(score: number): 'promoter' | 'passive' | 'detractor' {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
}

/**
 * Calculate time to value (hours from welcome email to first payment)
 */
export function calculateTimeToValue(
  welcomeSentAt: Timestamp,
  firstPaymentAt: Timestamp
): number {
  const diffMs = firstPaymentAt.toMillis() - welcomeSentAt.toMillis();
  return Math.round(diffMs / (1000 * 60 * 60)); // Convert to hours
}
